import React from 'react';
import SingleSelect from '@components/single-select';
import MenuItem from '@material-ui/core/MenuItem';
import InputField from 'components/pure/utils/form/InputField';
import Switch from '@components/switch';
import {List} from 'immutable';
import Department, {DepartmentId} from 'records/Department';
import {UserTableQuery} from '@components/admin/UserTableQueryParams';
import throttle from 'lodash/throttle';
import WebDataButton from 'components/utils/WebDataButton';
import WebDataRocksActions, {WebDataType} from '@store/web-data-rocks/WebDataRocksActions';
import {UserTableView} from '@store/@types/UserAdmin';
import moment from 'moment';

interface UserAdminToolbarProps {
  departmentProfiles: List<Department>;
  selectedDepartment?: DepartmentId;
  showArchived: boolean;
  searchText: string;
  users: List<UserTableView>;
  updateQuery: (query: UserTableQuery) => void;
  runSearch: () => Promise<List<UserTableView>>;
}
export function UserAdminToolbar(props: UserAdminToolbarProps) {
  const getWebData = (users: List<UserTableView>) => {
    const mappedUsers = users.map(user => {
      return {
        'Display Name': user.displayName,
        Team: user.teamName,
        Email: user.email,
        'Last Login': user.lastLogin
          ? moment(user.lastLogin).format(moment.localeData().longDateFormat('L'))
          : 'Never',
        'Login Enabled': user.active,
        Calls: user.hasCalls,
        'Calendar Connected': user.calendarConnected,
        'Start Date': moment().format(moment.localeData().longDateFormat('L')),
        'End Date': moment().format(moment.localeData().longDateFormat('L')),
      };
    });
    return mappedUsers.toJS();
  };

  const webDataOnClick = async () => {
    // Dialog needs to be visible before data is set so it renders the loading state during the request
    WebDataRocksActions.showDialog('Admin Users');
    const users = await props.runSearch();
    WebDataRocksActions.setData(WebDataType.ADMIN_USERS, getWebData(users));
  };

  return (
    <div className="tool-bar">
      <div className="filters">
        <div className="department-filter">
          <span className="department-label">Department: </span>
          <SingleSelect
            className="department-select"
            value={props.selectedDepartment ?? -1}
            onChange={e => {
              // Special case for all departments
              if (e.target.value === -1) {
                props.updateQuery({departmentId: undefined});
              } else {
                props.updateQuery({departmentId: e.target.value});
              }
            }}>
            <MenuItem value={-1} className="admin-department-dropdown-option">
              All
            </MenuItem>
            {props.departmentProfiles.map(d => (
              <MenuItem key={d.id} value={d.id} className="admin-department-dropdown-option">
                {d.name ?? ''}
              </MenuItem>
            ))}
          </SingleSelect>
        </div>
        <InputField
          className="user-search"
          type="text"
          onChange={search => {
            props.updateQuery({
              searchText: search as string,
            });
          }}
          placeholder="Filter"
          value={props.searchText}
        />
        <Switch
          label="Show Inactive Users"
          value={props.showArchived}
          onChange={throttle(
            () =>
              props.updateQuery({
                showArchived: !props.showArchived,
              }),
            1000
          )}></Switch>
      </div>
      <div className="actions">
        <WebDataButton onClick={webDataOnClick} tooltip="Export" />
      </div>
    </div>
  );
}
